<template>
  <modal-vue @close="handleToggleModal" :modalActive="modals[modalName]" :hasFooterBorder="false">
    <template #header>
      <div class="text-left px-[16px] flex">
        <strong class="title font-20">Compartilhar</strong>
      </div>
    </template>

    <v-row dense v-if="isUnauthorize">
      <v-alert
        color="error"
        icon="$warning"
        title="Você não tem permissão para compartilhar este recurso..."
      ></v-alert>
    </v-row>
    <v-row dense v-else>
      <v-col :md="subjectsToShare.length ? 10 : 12" cols="12">
        <v-autocomplete
          v-model="subjectsToShare"
          :filter-keys="['raw.title', 'raw.subtitle']"
          label="Adicionar Participantes ou Grupos"
          color="primary20"
          variant="outlined"
          density="compact"
          clear-on-select
          :menu-props="{ closeOnContentClick: true }"
          chips
          closable-chips
          multiple
          hide-details
          clearable
          :disabled="isLoading || !canEditCompanyAccess"
          :loading="isLoading"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              color="primary30"
              v-bind="props"
              :prepend-icon="item.raw.icon"
              :text="item.raw.title"
            />
          </template>

          <template v-slot:item="{ props, item }">
            <v-list-item
              v-bind="props"
              :prepend-icon="item.raw.icon"
              :subtitle="item.raw.subtitle"
              :title="item.raw.title"
            />
          </template>
        </v-autocomplete>
      </v-col>

      <v-col v-if="subjectsToShare.length" cols="12" md="2">
        <v-autocomplete
          v-model="permission"
          auto-select-first
          :items="permissionNewUsers"
          item-title="label"
          item-value="value"
          color="primary20"
          density="compact"
          hide-details
          chips
          variant="outlined"
          required
        >
          <template v-slot:chip="{ props, item }">
            <span class="flex items-center text-sm" v-bind="props">
              {{ item.raw.label }}
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <Transition name="slide-down" mode="out-in">
        <v-col class="mt-3">
          <v-textarea
            v-model="messageNotify"
            class="message-to-share"
            color="primary20"
            label="Mensagem"
            :disabled="!(subjectsToShare.length > 0)"
          />
          <v-checkbox
            v-model="isNotifyShare"
            color="primary"
            fieldId="isNotify"
            label="Notificar"
            :disabled="!messageNotify || messageNotify.trim() === ''"
          />
        </v-col>
      </Transition>
    </v-row>

    <template #footer>
      <Transition name="fade" mode="out-in">
        <v-row class="pt-3">
          <v-col cols="12" md="6">
            <ghost-button @click="handleToggleModal" width="100%" text="Cancelar" />
          </v-col>
          <v-col cols="12" md="6">
            <filled-button
              @click="() => {}"
              :text="isShareContext ? 'Enviar' : 'Concluido'"
              height="52px"
              width="100%"
              :disabled="
                isLoading || isUnauthorize || !(!messageNotify || messageNotify.trim() === '')
              "
            />
          </v-col>
        </v-row>
      </Transition>
    </template>
  </modal-vue>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import {
  PermissionResponse,
  PermissionType,
  ResourceType
} from '@/Network/Types/Responses/PermissionResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const modalName = EModals.SHARE_POLICY
const { modals } = storeToRefs(useModalStore())
const modalStore = useModalStore()
const permissionsStore = usePermissionsStore()

const canEditCompanyAccess = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.AccessLevelEditing,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})

const handleToggleModal = () => {
  clearFields()
  modalStore.toggleModal(modalName)
}
const isShareContext = ref(false)
const isUnauthorize = ref(false)

const permissionsData = ref<PermissionResponse[]>([])

const permission = ref<PermissionType>(PermissionType.Admin)
const permissionNewUsers = ref([
  {
    label: 'Leitor',
    value: PermissionType.View
  },
  {
    label: 'Editor',
    value: PermissionType.Admin
  }
])
const subjectsToShare = ref<{ id: string; type: ResourceType }[]>([])
const isNotifyShare = ref<boolean>(true)
const isLoading = ref<boolean>(false)
const messageNotify = ref<string>('')

function clearFields() {
  subjectsToShare.value = []
  permissionsData.value = []
  messageNotify.value = ''
  isUnauthorize.value = false
}
</script>
<style scoped>
.fade-enter-active {
  transition: opacity 0.25s;
}
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter {
  opacity: 0;
}

.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.message-to-share :deep(.v-field__input) {
  height: 220px;
}
</style>
